import { sendAmplitudeData, setAmplitudeUserId } from './amplitude'
import { pushBingEvent } from './bing'
import { sendFacebookPixelTrack } from './facebookPixel'
import { sendBIData, setBIUserId } from './BI'
import {
  ENV,
  GOOGLE_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
  GOOGLE_ADS_CONVERSION_ID,
  GOOGLE_ADS_LEAD_ID,
  PREDICTED_LTV_VALUE,
  UET_PURCHASE_GOAL,
} from '@constants'
import { type GoogleAdsUserData, hasGoogleAdsUserData } from './googleAdsUserData'
import { sendGoogleTagEvent } from './googleTag'

export { buildGoogleAdsUserData } from './googleAdsUserData'
export type { GoogleAdsUserData, GoogleAdsStripeBillingDetails } from './googleAdsUserData'

const isProd = ENV === 'production'
const isDev = ENV === 'development'

export const sendAnalyticsEvents = (
  eventName: string,
  eventParams: object,
  skipAdCabinets = false,
) => {
  sendAmplitudeData(eventName, eventParams)
  sendBIData(eventName, eventParams)
  if (!skipAdCabinets) {
    pushGoogleTag(eventName, eventParams)
  }
}

interface PurchaseEventProps {
  price: number
  currency?: string
  plan: string
  sub_id: string
  event_id: string
  gclid?: string
  pixelName: string
  skipAdCabinets?: boolean
  userData?: GoogleAdsUserData
}

export const sendPurchaseEvent = (props: PurchaseEventProps) => {
  const { price, currency, plan, sub_id, event_id, gclid, pixelName, userData } = props

  if (pixelName === 'fb') {
    sendFacebookPixelTrack(
      'Purchase' + (!isProd ? '_test' : ''),
      {
        currency: currency || 'USD',
        itemCount: 1,
        transactionId: sub_id,
        price: price,
        value: price,
      },
      { eventID: event_id },
    )
  } else if (pixelName === 'ttq') {
    window?.ttq?.track('CompletePayment', {
      value: price,
      currency: currency || 'USD',
      contents: [
        {
          content_type: 'product',
          content_name: plan,
        },
      ],
      event_id,
    })
  } else if (pixelName === 'bing') {
    pushBingEvent(
      UET_PURCHASE_GOAL,
      {
        revenue_value: PREDICTED_LTV_VALUE, // predicted instead of price
        currency: currency || 'USD',
        event_category: UET_PURCHASE_GOAL,
      },
      event_id,
    )
  }

  if (!isDev) {
    if (GOOGLE_MEASUREMENT_ID) {
      sendGoogleTagEvent('purchase', {
        currency: currency || 'USD',
        value: price,
        transaction_id: sub_id,
        gclid,
        items: {
          item_id: plan,
          item_name: plan,
        },
        send_to: GOOGLE_MEASUREMENT_ID,
      })
    }
    if (GOOGLE_ADS_ID && GOOGLE_ADS_CONVERSION_ID) {
      sendGoogleTagEvent('conversion', {
        send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_ID}`,
        value: PREDICTED_LTV_VALUE, // predicted instead of price,
        currency: currency || 'USD',
        transaction_id: event_id,
        ...(userData && hasGoogleAdsUserData(userData) ? { user_data: userData } : {}),
      })
    }
  }
}

export const pushGoogleLeadTag = (userData?: GoogleAdsUserData) => {
  if (isDev) return
  if (!GOOGLE_ADS_ID || !GOOGLE_ADS_LEAD_ID) return
  sendGoogleTagEvent('conversion', {
    send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_LEAD_ID}`,
    ...(userData && hasGoogleAdsUserData(userData) ? { user_data: userData } : {}),
  })
}

export const setAnalyticsUserId = (userId: string) => {
  setAmplitudeUserId(userId)
  setBIUserId(userId)
}

export const pushGoogleTag = (
  eventName: string,
  eventParams: object,
  tagId = GOOGLE_MEASUREMENT_ID,
) => {
  if (isDev || !tagId) return
  sendGoogleTagEvent(eventName, { ...eventParams, send_to: tagId })
}
